/** Breathing room kept between the bubble and the edge it would otherwise touch. */
const EDGE_MARGIN = 8;

/**
 * How far to slide a centred tooltip bubble so it stays inside its clipping edges.
 *
 * The bubble is centred on a 16px icon and can be 300px wide, so any trigger within ~150px of an
 * edge that hides overflow loses text — on the antenatal form the first letter of every line of the
 * Presentation hint was cut off. Sliding the bubble keeps it whole; the alternative, asking each of
 * the eleven call sites to declare an alignment, goes stale the moment a grid reflows.
 *
 * Only one edge can win: a bubble wider than the space it sits in is pinned to the left, because
 * text that starts at the beginning can still be read.
 */
export const clampTooltipShift = ({
  triggerCenterX,
  bubbleWidth,
  clipLeft,
  clipRight,
}) => {
  const left = triggerCenterX - bubbleWidth / 2;
  const right = triggerCenterX + bubbleWidth / 2;

  const overflowLeft = clipLeft + EDGE_MARGIN - left;
  if (overflowLeft > 0) return overflowLeft;

  const overflowRight = right - (clipRight - EDGE_MARGIN);
  if (overflowRight > 0) return -overflowRight;

  return 0;
};

/**
 * The nearest ancestor that would clip the bubble, as a pair of x bounds.
 *
 * Falls back to the viewport, which is itself a clipping edge — a tooltip on a wide screen with no
 * scroll container still must not run off the window.
 */
export const findClipBounds = (element) => {
  let node = element?.parentElement;

  while (node && node !== document.body) {
    const { overflowX, overflow } = window.getComputedStyle(node);
    if (/(hidden|auto|scroll|clip)/.test(`${overflowX} ${overflow}`)) {
      const { left, right } = node.getBoundingClientRect();

      return { clipLeft: left, clipRight: right };
    }

    node = node.parentElement;
  }

  return { clipLeft: 0, clipRight: window.innerWidth };
};
